



















































import Vue from 'vue';
import { Route } from 'vue-router';
import {
  defineComponent,
  ref,
  Ref,
  toRefs,
  watch,
  onMounted,
  reactive,
  ComponentInternalInstance,
  getCurrentInstance,
  ComponentInstance,
} from '@vue/composition-api';
import { SettingIcon } from 'tdesign-icons-vue';
import _ from 'lodash';
import BaseIcon from '@/renderer/components/common/BaseIcon.vue';
import { IpcEvents } from '@/ipc-events';
import { IS_BROWSER, IS_PC_IN_MAC } from '@/common/constant/platform';
import ipc from '@/renderer/scripts/utils/ipc';
import storage from '@/renderer/scripts/utils/storage/index';
import '@/renderer/assets/images/Icon/svg/remove.svg';
import '@/renderer/assets/images/Icon/svg/fullscreen.svg';
import '@/renderer/assets/images/Icon/svg/fullscreen-exit.svg';
import '@/renderer/assets/images/Icon/svg/close.svg';

/**
 * 窗口尺寸
 */
type WindowSize = {
  width: number;
  height: number;
};

/**
 * 窗口尺寸预设
 * 包含默认尺寸和允许的最小尺寸
 */
type WindowSizePreset = {
  minimum: WindowSize;
  default: WindowSize;
};

/**
 * 窗口状态
 * 包含是否全屏、是否最大化和窗口尺寸预设
 */
type WindowState = {
  isFullScreen: boolean;
  isMaximize: boolean;
  windowSizePreset: WindowSizePreset;
};

/**
 * 窗口尺寸预设的集合
 * 设定了 small、middle 两种预设
 */
type WindowSizePresetSet = {
  small: WindowSizePreset;
  middle: WindowSizePreset;
};

/**
 * 路由相关的逻辑块
 * 主要用于判断当前路由是否处在 login 页面
 */
const handleRoute = (vm: ComponentInstance) => {
  // 依赖路由页名判断 isLogined
  // 当且仅当处在 Login/register 页面时，认为 isLogined=false 未登录
  const isLogined: Ref<boolean> = ref(
    typeof vm.$route.name === 'string' && !['login', 'register'].includes(vm.$route.name as string),
  );
  watch(
    (): Route => vm.$route,
    (route: Route): void => {
      isLogined.value = typeof vm.$route.name === 'string' && !['login', 'register'].includes(vm.$route.name as string);
    },
  );
  return {
    isLogined,
  };
};

/**
 * 窗口相关的逻辑块
 * 主要包含控制窗口行为的方法和参数
 */
const handleWindow = (vm: ComponentInstance, isLogined: Ref<boolean>) => {
  /**
   * 窗口尺寸预设集合
   * 状态｜默认大小｜最小
   * small｜1050x700｜1050x700
   * middle｜1440x960｜1050x700
   * 注：为保证其他样式正常显示，不考虑比预设尺寸小的屏幕
   */
  const WINDOW_SIZE_PRESET_SET: WindowSizePresetSet = {
    middle: {
      minimum: {
        width: 1050,
        height: 700,
      },
      default: {
        width: 1440,
        height: 960,
      },
    },
    small: {
      minimum: {
        width: 1050,
        height: 700,
      },
      default: {
        width: 1050,
        height: 700,
      },
    },
  };

  /**
   * 窗口的实时状态
   */
  const windowState = reactive({
    isFullScreen: false,
    // 判断当前窗口是否全屏,
    isMaximize: false,
    // 判断当前窗口是否最大化
    windowSizePreset: WINDOW_SIZE_PRESET_SET.small, // 当前窗口的尺寸（尺寸和最小尺寸）
  });

  /**
   * 窗口状态相关的操作方法
   */
  const windowStateMethods = {
    /**
     * 设置是否全屏
     */
    setFullScreen: (isFull: boolean) => {
      ipc.send(IpcEvents.ON_OPERATE_MAIN_LISTENER, {
        event: 'setFullScreen',
        params: {
          isFull,
        },
      });
    },
    /**
     * 窗口最大化
     */
    setWindowMaximize: (isMaximize: boolean) => {
      if (isMaximize) {
        ipc.send(IpcEvents.ON_OPERATE_MAIN_LISTENER, {
          event: 'winMaximize',
        });
      } else {
        ipc.send(IpcEvents.ON_OPERATE_MAIN_LISTENER, {
          event: 'winUnmaximize',
        });
      }
    },
    /**
     * 窗口最小化
     */
    setWindowMinimize: () => {
      ipc.send(IpcEvents.ON_OPERATE_MAIN_LISTENER, {
        event: 'winMin',
      });
    },
    /**
     * 关闭窗口
     */
    setWindowClose: async (isLogined: boolean) => {
      const storageSystemSetting: any = storage.get(storage.StoreKey.SYSTEM_SETTING);
      ipc.send(IpcEvents.ON_OPERATE_MAIN_LISTENER, {
        event: 'winClose',
        params: {
          isLogined,
          minToTray: storageSystemSetting.minToTray,
        },
      });
    },
    /**
     * 设置允许用户缩放的最小窗口尺寸
     */
    setWindowMinimumSize: (size: WindowSize) => {
      ipc.send(IpcEvents.ON_OPERATE_MAIN_LISTENER, {
        event: 'setMinimumSize',
        params: size,
      });
    },
    /**
     * 设置窗口尺寸
     */
    setWindowSize: (size: WindowSize) => {
      ipc.send(IpcEvents.ON_OPERATE_MAIN_LISTENER, {
        event: 'setWinSize',
        params: size,
      });
    },
    /**
     * 设置窗口到屏幕中心
     */
    setWindowCenter: () => {
      ipc.send(IpcEvents.ON_OPERATE_MAIN_LISTENER, {
        event: 'setWinCenter',
      });
    },
    /**
     * 聚合操作语法糖
     * 一次性设定是否全屏、是否最大化、窗口尺寸
     */
    setWindowState: (windowState: WindowState) => {
      windowStateMethods.setFullScreen(windowState.isFullScreen);
      windowStateMethods.setWindowMaximize(windowState.isMaximize);
      windowStateMethods.setWindowMinimumSize(windowState.windowSizePreset.minimum);
      /**
       * 在执行 setWindowSize 之前需要判断 isFullScreen 与 isMaximize 是必要的
       * 如无，将出现这种情况：
       *  窗口本应恢复成全屏/最大化状态，
       *  执行完成 setFullScreen() 与 setWindowMaximize() 后，已经恢复成全屏/最大化状态，
       *  但又被 setWindowSize() 覆盖，不符合预期。
       * 故仅在既无全屏又无最大化的情况下执行 setWindowSize()
       */
      !windowState.isFullScreen &&
        !windowState.isMaximize &&
        windowStateMethods.setWindowSize(windowState.windowSizePreset.default);
    },
  };

  /**
   * 设置有关窗口的允许策略
   * 即是否允许——
   *  1. 全屏(MacOS)
   *  2. 窗口最大化/还原
   *  3. 拖动以设置窗口大小
   * 以上均为未登录不允许，登录后允许
   */
  const setWindowAllowPolicy = (isAble: boolean): void => {
    const ableEventNameList = ['setFullScreenAble', 'setWinMaximizAble', 'setWinResizAble'];
    ableEventNameList.forEach(event => {
      ipc.send(IpcEvents.ON_OPERATE_MAIN_LISTENER, {
        event,
        params: {
          isAble,
        },
      });
    });
  };

  /**
   * 固化/恢复 windowState <-> windowStateStorage
   */
  const windowStateStorage = {
    set: (windowState: WindowState) => {
      storage.set(storage.StoreKey.WINDOW_STATE, windowState);
    },
    // eslint-disable-next-line max-len
    get: (): WindowState => storage.get(storage.StoreKey.WINDOW_STATE) as WindowState,
  };
  const onWndReSize = _.debounce(
    (wndSize: WindowSize) => {
      if (!isLogined.value || IS_BROWSER) return;
      windowState.windowSizePreset.default = wndSize;
      windowStateStorage.set(windowState);
    },
    2000,
    {
      leading: true,
    },
  );
  onMounted(() => {
    Object.assign(windowState, windowStateStorage.get());

    // 监听是否窗口最大化
    ipc.on(IpcEvents.WINDOW_MAXIMIZE_MAXIMIZE, (e, args) => {
      if (!vm.$store.getters.isLogined || IS_BROWSER) return;
      const { isMaximize: value } = args;
      windowState.isMaximize = value;
      windowStateStorage.set(windowState);
    });
    // 监听是否全屏
    ipc.on(IpcEvents.FULL_SCREEN, (e, args) => {
      // 由于现在记录FULL_SCREEN后恢复的逻辑有问题(会不生效)，这里先设置为false，避免影响窗口大小逻辑
      // 全屏逻辑需要之后再修复
      windowState.isFullScreen = false;
      windowStateStorage.set(windowState);
    });
    // 监听窗口大小
    ipc.on(IpcEvents.WINDOW_RESIZE, (e, args) => {
      if (!vm.$store.getters.isLogined || IS_BROWSER) return;
      // 记录登陆状态下，窗口大小调整

      const { windowSizePreset: value } = args;
      onWndReSize(value.default);
    });
  });
  return {
    windowState,
    WINDOW_SIZE_PRESET_SET,
    setWindowAllowPolicy,
    windowStateMethods,
    windowStateStorage,
  };
};
export default defineComponent({
  components: {
    BaseIcon,
    SettingIcon,
  },
  setup(props, context) {
    const currComponent: ComponentInternalInstance = getCurrentInstance() as ComponentInternalInstance;
    const vm = currComponent.proxy;

    // 自制操作栏
    const showCloseOperate: Ref<boolean> = ref(!IS_PC_IN_MAC && !IS_BROWSER);
    const { isLogined } = handleRoute(vm);
    const { windowState, WINDOW_SIZE_PRESET_SET, setWindowAllowPolicy, windowStateMethods, windowStateStorage } =
      handleWindow(vm, isLogined);
    const handleLoginChange = async (isLogin: boolean) => {
      if (!isLogin) {
        // 当[已登录 -注销-> 未登录]时
        // 1. 退出全屏（无需关心是否全屏）
        // 2. 还原最大化（无需关心是否最大化）
        // 3. 设定窗口尺寸为 small 预设
        windowStateMethods.setWindowState({
          isFullScreen: false,
          isMaximize: false,
          windowSizePreset: WINDOW_SIZE_PRESET_SET.small,
        });
        setTimeout(() => {
          windowStateMethods.setWindowCenter();
        }, 50);
      } else {
        // 当[未登录 -登录-> 已登录]时
        // - 如果有固化的 windowStateStorage 数据则恢复窗口状态
        // - 如果没有，则使用默认策略
        Object.assign(windowState, windowStateStorage.get());
        const lastWndSize = windowState.windowSizePreset.default;
        const screenSize = await ipc.invoke(IpcEvents.INVOKE_OPERATE_MAIN_LISTENER, {
          event: 'getScreenSize',
        });
        lastWndSize.height = Math.min(lastWndSize.height, screenSize.height);
        lastWndSize.width = Math.min(lastWndSize.width, screenSize.width);
        windowState.windowSizePreset.default = lastWndSize;
        windowStateMethods.setWindowState(windowState);
        setTimeout(() => {
          windowStateMethods.setWindowCenter();
        }, 50);
      }
      setWindowAllowPolicy(isLogin);
    };
    handleLoginChange(isLogined.value);
    setTimeout(() => {
      setWindowAllowPolicy(isLogined.value); // 延迟设置，规避重复设置导致最大化顶部UI异常
    }, 1000);
    const methods = {
      openSetting() {
        ipc.emit(IpcEvents.MENU_CLICK, undefined, {
          event: 'setting',
        });
      },
    };
    watch(isLogined, async newValue => {
      handleLoginChange(newValue);
    });
    return {
      showCloseOperate,
      isLogined,
      ...toRefs(windowState),
      ...windowStateMethods,
      ...methods,
    };
  },
});
